import { QueriesOptions, UseQueryOptions, useQueries as useReactQueries } from '@tanstack/react-query';
import { DocumentNode } from 'graphql';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AnyVariables,
  Client,
  OperationContext,
  OperationResult,
  TypedDocumentNode,
  UseQueryArgs as UrqlUseQueryArgs,
  UseQueryResponse as UrqlUseQueryResponse,
  UseQueryState as UrqlUseQueryState,
  UseMutationResponse,
  useClient,
  useMutation as useUrqlMutation,
  useQuery as useUrqlQuery,
} from 'urql';
import { useDeepMemo, usePolling, useRetryWithBackoff } from '@/utils';

export type UseQueryArgs<Variables extends AnyVariables = AnyVariables, Data = unknown> = Omit<
  UrqlUseQueryArgs<Variables, Data>,
  'context'
> & {
  context: Partial<OperationContext>;
  pollInterval?: number;
  autoRetryErrors?: boolean;
};

export interface UseQueryState<Data, Variables extends AnyVariables = AnyVariables>
  extends UrqlUseQueryState<Data, Variables> {
  secondsUntilRetry?: number;

  /** If true, the query is loading for the first time */
  isLoading: boolean;

  /** If true, a fetch is in progress and returns cached data  */
  isFetching: boolean;
}

export type UseQueryResponse<Data, Variables extends AnyVariables = AnyVariables> = [
  state: UseQueryState<Data, Variables>,
  refetch: UrqlUseQueryResponse<Data, Variables>[1],
];

/**
 * Urql's useQuery hook, but with some extra arguments/options for polling,
 * auto retry of failed queries, and improved loading states (isLoading, isFetching).
 *
 * LOADING STATES & DATA HANDLING:
 * - `isLoading`: true only during the initial load (no cached data yet)
 *   - Use this to show initial loading indicators
 * - `isFetching`: true whenever a fetch is in progress (initial load or refetch)
 *   - Returns cached data while refetching
 *   - Use this to show loading indicators while keeping the data visible.
 * - `fetching`: original urql property (same as isFetching) - kept for compatibility
 */
export function useQuery<Data = unknown, Variables extends AnyVariables = AnyVariables>(
  args: UseQueryArgs<Variables, Data>
): UseQueryResponse<Data, Variables> {
  const [state, refetch] = useUrqlQuery(args as UrqlUseQueryArgs<Variables, Data>);

  // Callback to force urql to reload data from the server, skipping the cache
  const refetchFromNetwork = useCallback(
    (opts?: Partial<OperationContext>) => {
      refetch({ requestPolicy: 'network-only', ...opts });
    },
    [refetch]
  );

  // Fetching will be true for initial load and when we have done a refetch
  // with 'network-only'. Stale will be true when urql is reloading data
  // because the cached data was invalidated by a mutation
  const requestInProgress = state.fetching || state.stale;

  // Poll if configured, disabling polling while paused, a request is in
  // progress, or while we're in an error state
  let pollInterval = args.pollInterval;
  if (args.pause === true || requestInProgress || state.error !== undefined) {
    pollInterval = undefined;
  }
  usePolling(refetchFromNetwork, pollInterval);

  // Automatically retry errors if configured, passing undefined for the error
  // to retry if paused or not configured
  let errorToRetry = state.error;
  if (args.pause === true || args.autoRetryErrors !== true) {
    errorToRetry = undefined;
  }
  const secondsUntilRetry = useRetryWithBackoff(refetchFromNetwork, requestInProgress, errorToRetry);

  const isLoading = state.fetching && (state.data === undefined || state.data === null); // true when fetching and no data has been loaded yet
  const isFetching = state.fetching; // true whenever a fetch is in progress

  // Add our extra pieces of state to the urql state and return response
  const response: UseQueryResponse<Data, Variables> = useMemo(() => {
    return [
      {
        ...state,
        data: state.data,
        secondsUntilRetry,
        isLoading,
        isFetching,
      },
      refetchFromNetwork,
    ];
  }, [state, refetchFromNetwork, secondsUntilRetry, isLoading, isFetching]);

  return response;
}

interface UrqlQueryArgs<Data, Variables extends AnyVariables = AnyVariables> {
  query: DocumentNode | TypedDocumentNode<Data, Variables> | string;
  variables?: Variables;
  context?: Partial<OperationContext>;
}

// Get base query option type
type BaseUseQueriesOption<Data extends unknown[]> = QueriesOptions<Data>[number];
// Remove "queryFn" attribute and add urqlQueryArgs for defining queryFn
export type UseQueriesOption<Data, Variables extends AnyVariables = AnyVariables> = Omit<
  BaseUseQueriesOption<Array<{ queryKey: string[]; queryFn: () => Promise<OperationResult<Data, Variables>> }>>,
  'queryFn'
> & {
  queryArgs: UrqlQueryArgs<Data, Variables>;
  onResponseSuccess?: (response: OperationResult<Data, Variables>) => OperationResult<Data, Variables>;
};

/**
 * Custom implementation using react-query's useQueries that allows execution of parallel dynamic queries.
 * Each queries object accepts a `queryArgs` attribute in place of `queryFn`.
 */
export function useQueries<Data = unknown, Variables extends AnyVariables = AnyVariables>({
  queries,
  context,
}: {
  queries: ReadonlyArray<UseQueriesOption<Data, Variables>>;
  context?: UseQueryOptions['context'];
}) {
  const client = useClient();
  const mappedQueries = queries.map(({ onResponseSuccess = (response) => response, queryArgs, ...rest }) => {
    return {
      ...rest,
      queryFn: async () => {
        const response = await client
          .query<Data, Variables>(queryArgs.query, queryArgs.variables as Variables, queryArgs.context)
          .toPromise();
        return onResponseSuccess(response);
      },
    };
  });
  return useReactQueries({ queries: mappedQueries, context: context });
}

/**
 * Just like urql's useMutation, but will throw when executing the mutation if
 * it results in an Error. (Errors thrown will be an urql CombinedError).
 */
export function useMutation<Data, Variables extends AnyVariables = AnyVariables>(
  query: DocumentNode | TypedDocumentNode<Data, Variables> | string
): UseMutationResponse<Data, Variables> {
  const [state, callback] = useUrqlMutation<Data, Variables>(query);

  // Wrap the callback that executes the mutation and throw the error if the
  // mutation results in an error
  const wrapped: typeof callback = async (...execArgs) => {
    const result = await callback(...execArgs);
    if (result.error) {
      throw result.error;
    }
    return result;
  };

  return [state, wrapped];
}

type UseLazyQueryArgs<Data, Variables extends AnyVariables = AnyVariables> = {
  query: DocumentNode | TypedDocumentNode<Data, Variables> | string;
};

type ExecuteQueryArgs<Variables extends AnyVariables = AnyVariables> = {
  variables?: Variables;
  context?: Partial<OperationContext>;
};

type LazyQueryState<Data, Variables extends AnyVariables = AnyVariables> = Pick<
  UseQueryState<Data, Variables>,
  'fetching' | 'error' | 'data'
>;

type UseLazyQueryResult<Data, Variables extends AnyVariables = AnyVariables> = [
  LazyQueryState<Data, Variables>,
  (args: ExecuteQueryArgs<Variables>) => Promise<void>,
];

const INITIAL_QUERY_STATE = { fetching: false };

/**
 * An urql version of Apollo's useLazyQuery hook, for manually invoking a
 * query and supplying variable values at execution time.
 */
export function useLazyQuery<Data, Variables extends object = object>(
  args: UseLazyQueryArgs<Data, Variables>
): UseLazyQueryResult<Data, Variables> {
  const client = useClient();

  const [queryState, setQueryState] = useState<LazyQueryState<Data, Variables>>(INITIAL_QUERY_STATE);
  const inFlightAbort = useRef<AbortController>();

  args = useDeepMemo(() => args, [args]);

  // Cancel any inflight requests and reset state if hook inputs change and on unmount
  useEffect(() => {
    return () => {
      if (inFlightAbort.current !== undefined) {
        inFlightAbort.current.abort();
        inFlightAbort.current = undefined;
      }
      setQueryState(INITIAL_QUERY_STATE);
    };
  }, [args]);

  // Callback to kick off the query
  const executeQuery = useCallback(
    async (execArgs: ExecuteQueryArgs<Variables>): Promise<void> => {
      // Cancel any in-flight requests
      if (inFlightAbort.current !== undefined) {
        inFlightAbort.current.abort();
      }

      // Re-initialize abort state
      const abortController = new AbortController();
      inFlightAbort.current = abortController;

      setQueryState({
        fetching: true,
        error: undefined,
        data: undefined,
      });

      // Attach abort controller to request context
      const ctx: typeof execArgs.context = { ...execArgs.context };
      ctx.fetchOptions ??= {};
      if (typeof ctx.fetchOptions === 'function') {
        ctx.fetchOptions = ctx.fetchOptions();
      }
      ctx.fetchOptions.signal = abortController.signal;

      // Network errors come back in result.error along with GraphQL errors, so
      // no need to try/catch here
      const result = await client.query(args.query, execArgs.variables as Variables, execArgs.context).toPromise();

      // Only do the side-effect to set state if we haven't been aborted
      if (abortController.signal.aborted === false) {
        setQueryState({
          fetching: false,
          error: result.error,
          data: result.data,
        });
      }
    },
    [client, args]
  );

  return [queryState, executeQuery];
}

type PaginationVariables = {
  after: string | null | undefined;
  pageSize: number;
};

const defaultGetPaginationVariables = ({ after, pageSize }: PaginationVariables) => ({ after, pageSize });

/**
 * usePaginatedQuery() is a hook to help manage server side GraphQL
 * queries that return a cursor-based pagination response. It handles translating
 * page numbers to cursors, and provides a way to fetch the next and previous page of data.
 */
export function usePaginatedQuery<T, TRowData extends unknown[], TVariables = object>({
  query,
  defaultPageSize = 100,
  additionalVariables,
  context,
  getPaginationData,
  getPaginationVariables = defaultGetPaginationVariables,
  append = false,
  pause = false,
}: {
  query: TypedDocumentNode<T, object>;
  additionalVariables?: Partial<TVariables>;
  defaultPageSize?: number;
  context: Partial<OperationContext>;
  getPaginationVariables?: (data: PaginationVariables) => object;
  getPaginationData: (data: T) => {
    rows?: TRowData;
    rowCount: number;
    cursor: string | null;
    hasNextPage?: boolean;
  };
  /**
   * If set to true, new pages fetched will be appended to the current list of rows
   */
  append?: boolean;
  pause?: boolean;
}) {
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [page, setPage] = useState(0);
  const accumulatedRowsRef = useRef<TRowData>([] as unknown as TRowData);
  const processedPagesRef = useRef<Set<number>>(new Set());

  const resetCursor = useCallback(() => {
    setAllCursors([null]);
    setPage(0);
    accumulatedRowsRef.current = [] as unknown as TRowData;
    processedPagesRef.current = new Set();
  }, []);

  const updatePageSize = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
      resetCursor();
    },
    [resetCursor]
  );

  const addCursorToMemory = (cursor: string) => {
    if (!allCursors.includes(cursor)) {
      setAllCursors(allCursors.concat(cursor));
    }
  };

  const [allCursors, setAllCursors] = useState<Array<string | null>>([null]);
  const currentCursor = allCursors[page];

  const paginationVariables = getPaginationVariables({
    pageSize,
    after: currentCursor,
  });

  const [{ data, fetching, error, stale }] = useQuery<T>({
    query,
    variables: { ...paginationVariables, ...additionalVariables },
    pause,
    context,
  });

  const {
    rows = [] as unknown as TRowData,
    rowCount = 0,
    cursor = null,
    hasNextPage = undefined,
  } = data ? getPaginationData(data) : {};

  // Handle data accumulation when append mode is enabled
  const displayRows = useMemo(() => {
    if (!append) {
      // Reset accumulated data when not in append mode
      accumulatedRowsRef.current = [] as unknown as TRowData;
      processedPagesRef.current = new Set();
      return rows;
    }

    // In append mode, accumulate data when we have new data for a page we haven't processed
    if (!fetching && rows.length > 0 && !processedPagesRef.current.has(page)) {
      accumulatedRowsRef.current = [...accumulatedRowsRef.current, ...rows] as TRowData;
      processedPagesRef.current.add(page);
    }

    return accumulatedRowsRef.current;
  }, [append, rows, fetching, page]);

  /**
   * Handles updating the page and pageSize together, which is a common pattern
   * when using with v6 MUI datagrid.
   */
  const setPageState = (newState: { page: number; pageSize: number }) => {
    if (newState.pageSize !== pageSize) {
      updatePageSize(newState.pageSize);
      return;
    }

    if (newState.page !== page) {
      // if a "hasNextPage" is provided, use that to determine if another page is availble
      // otherwise just use the newState page
      // https://github.com/chronosphereio/monorepo/pull/57499
      if (hasNextPage === undefined) {
        setPage(newState.page);
      } else if (hasNextPage || newState.page <= page) {
        setPage(newState.page);
      }
    }
  };

  useEffect(() => {
    if (cursor != null) {
      addCursorToMemory(cursor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  return {
    pageSize,
    setPageSize: updatePageSize,
    page,
    setPage,
    setPageState,
    fetching,
    error,
    rows: displayRows,
    rowCount,
    cursor: currentCursor,
    endCursor: cursor,
    hasNextPage: hasNextPage === undefined ? false : hasNextPage,
    stale,
    /** Convenience function for consumers to reset the cursor list, e.g. if filters change and the previously set
     * cursors are no longer valid. Also sets the page to 0.
     */
    resetCursor,
  };
}

export const DEFAULT_PAGE_SIZE = 100;

/**
 * usePaginatedQueryGetFullList() is a hook to return all data from server side GraphQL queries that return a
 * cursor-based pagination response. It iterates through all pages, translating page numbers
 * to cursors. All results are concatenated together and the entire result is returned.
 */
export function usePaginatedQueryGetFullList<T, TRow, TVariables = object>({
  query,
  additionalVariables,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  context,
  getPaginationData,
  maxFetchCount = Infinity,
  pause = false,
}: {
  query: TypedDocumentNode<T, object>;
  additionalVariables?: Partial<TVariables>;
  defaultPageSize?: number;
  context: Partial<OperationContext>;
  getPaginationData: (data: T) => {
    rows?: TRow[];
    rowCount: number;
    cursor: string | null;
    hasNextPage?: boolean;
  };
  maxFetchCount?: number;
  pause?: boolean;
}) {
  const [variables, setVariables] = useState({
    after: '',
    pageSize: defaultPageSize,
    filters: null,
  });

  const {
    rows,
    rowCount,
    endCursor,
    hasNextPage,
    fetching: loading,
    error,
    stale,
  } = usePaginatedQuery({
    query,
    getPaginationData,
    pause: variables.pageSize === 0 || pause,
    additionalVariables: { ...variables, ...additionalVariables },
    context,
  });

  const results = useRef<TRow[]>([]);
  const mergedResults = useRef<TRow[]>([]);
  const fetchCount = useRef<number>(0);
  const isLoading = useRef(false);
  const isStale = useRef(false);

  /**
   * Checking to see if a fetch was just made before setting data (the same data we may already have)
   */
  const completedFetch = !loading && isLoading.current;
  const completedRefetch = !stale && isStale.current;

  if ((completedFetch || completedRefetch) && !isEqual(rows, mergedResults.current)) {
    fetchCount.current = fetchCount.current + 1;
    /**
     * If the variable 'after' is set to an emtpy string, we know that we are on
     * the first page of a paginated list. This means that we should replace any existing
     * items with the new results. Otherwise, we'll append to the existing list.
     */
    if (variables.after === '') {
      results.current = rows;
    } else {
      results.current = results.current.concat(rows);
    }
    mergedResults.current = rows;
  }

  if (isLoading.current !== loading) {
    isLoading.current = loading;
  }
  if (isStale.current !== stale) {
    isStale.current = stale;
  }

  useEffect(() => {
    if (
      !loading &&
      !stale &&
      hasNextPage &&
      endCursor &&
      endCursor !== variables.after &&
      fetchCount.current < maxFetchCount
    ) {
      setVariables({ ...variables, after: endCursor });
    }
  }, [endCursor, hasNextPage, loading, maxFetchCount, stale, variables]);

  return {
    results: results.current,
    loading: fetchCount.current < maxFetchCount && (hasNextPage || loading || stale),
    totalCount: rowCount ?? 0,
    error,
  };
}

/**
 * Execute a query using a provided urql client.
 */
export const executeQuery = async <TData, TVariables extends object>(
  client: Client,
  query: string | TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  context?: Partial<OperationContext>
): Promise<TData> => {
  const result = await client.query<TData, TVariables>(query, variables, context).toPromise();
  if (result.error) throw result.error;
  if (!result.data) throw new Error('No data');
  return result.data;
};

/**
 * Execute a mutation using a provided urql client.
 */
export const executeMutation = async <TData, TVariables extends object>(
  client: Client,
  mutation: string | TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  context?: Partial<OperationContext>
): Promise<TData> => {
  const result = await client.mutation<TData, TVariables>(mutation, variables, context).toPromise();
  if (result.error) throw result.error;
  if (!result.data) throw new Error('No data');
  return result.data;
};
